import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';


const AVAILABLE_MODELS = [
  {
    name: 'rerank_polymkt1',
    type: 'rerank',
    endpoint: '/scoring/ranking_model/polymarket-rerank-v1',
  },
  {
    name: 'poly_cf1',
    type: 'rerank',
    endpoint: '/scoring/ranking_model/polymarket-cf1',
  },
];

const ScoringQueryColumn = () => {
  const { defaultPolymarketWalletAddress, mergeCurrentContext } = useGlobal();
  const { hits, setHits, scoringApi, isLoading } = useBuild();
  
  const [selectedModel, setSelectedModel] = useState(AVAILABLE_MODELS[0]);
  const [userId, setUserId] = useState('');

  useEffect(() => {
    mergeCurrentContext({
      scoringModel: selectedModel?.name,
      scoringUserId: userId || undefined,
    });
    return () => mergeCurrentContext({ scoringModel: undefined, scoringUserId: undefined });
  }, [selectedModel, userId, mergeCurrentContext]);

  // Set default user ID when component mounts
  useEffect(() => {
    if (defaultPolymarketWalletAddress) {
      setUserId(defaultPolymarketWalletAddress);
    }
  }, [defaultPolymarketWalletAddress]);

  const handleExecuteQuery = async () => {
    if (!userId.trim()) {
      return;
    }

    try {
      if (!hits || hits.length === 0) {
        return;
      }

      // Extract item IDs from search results
      const itemIds = hits.map((hit) => hit._id);
      
      const payload = {
        user_id: userId,
        item_ids: itemIds,
      };

      console.log('Sending Scoring Query:', selectedModel.endpoint, payload);
      const response = await scoringApi.getScores(selectedModel.endpoint, payload);
      console.log('API Response:', response);
      
      const result = response?.result;
      if (!result || !Array.isArray(result)) {
        return;
      }

      const scoreByItemId = {};
      result.forEach(({ id, score }) => { scoreByItemId[String(id)] = score; });

      // Add scores to hits
      const modelName = selectedModel.name;
      for (const hit of hits) {
        const hitId = hit._id;
        const hitScore = scoreByItemId[String(hitId)];
        if (hitScore != null) {
          if (!hit._scores) {
            hit._scores = {};
          } 
          hit._scores[modelName] = hitScore;
        }
      }
      setHits([...hits]);
    } catch (error) {
      console.error('Error fetching scores:', error);
    }
  };

  const isQueryValid = userId.trim().length > 0;

  return (
    <Paper elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Compose Query
      </Typography>
      
      <Stack spacing={2} sx={{ flex: 1 }}>
        {/* Model Selection */}
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel>Model</InputLabel>
          <Select
            value={selectedModel.name}
            onChange={(e) => {
              const model = AVAILABLE_MODELS.find(m => m.name === e.target.value);
              setSelectedModel(model);
            }}
            label="Model"
          >
            {AVAILABLE_MODELS.map((model) => (
              <MenuItem key={model.name} value={model.name}>
                {model.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* User Id Input */}
        <TextField
          label="User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Enter user ID"
          sx={{ bgcolor: 'background.paper' }}
        />

        {/* Execute Query Button */}
        <Box sx={{ mt: 'auto' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExecuteQuery}
            disabled={!isQueryValid || isLoading}
            fullWidth
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Executing...
              </>
            ) : (
              'Execute Query'
            )}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};

export default ScoringQueryColumn;